import { useCallback, useMemo, useState } from 'react';
import type { TourStep } from '@/types/help-tour';

export interface UseScreenTourOptions {
  steps: TourStep[];
  completedStorageKey: string;
  welcomeStorageKey?: string;
  /** Reduce or reorder steps based on current page state (e.g. empty lists). */
  resolveSteps?: (steps: TourStep[]) => TourStep[];
}

export function useScreenTour({ steps, completedStorageKey, welcomeStorageKey, resolveSteps }: UseScreenTourOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [showWelcome, setShowWelcome] = useState(
    () => welcomeStorageKey != null && localStorage.getItem(welcomeStorageKey) !== '1'
  );

  const activeSteps = useMemo(() => (resolveSteps ? resolveSteps(steps) : steps), [resolveSteps, steps]);

  const finishTour = useCallback(() => {
    setIsOpen(false);
    setStepIdx(0);
    localStorage.setItem(completedStorageKey, '1');
  }, [completedStorageKey]);

  const startTour = useCallback(() => {
    if (welcomeStorageKey) {
      localStorage.setItem(welcomeStorageKey, '1');
      setShowWelcome(false);
    }
    setStepIdx(0);
    setIsOpen(true);
  }, [welcomeStorageKey]);

  const dismissWelcome = useCallback(() => {
    if (welcomeStorageKey) {
      localStorage.setItem(welcomeStorageKey, '1');
    }
    setShowWelcome(false);
  }, [welcomeStorageKey]);

  const goNext = useCallback(() => {
    setStepIdx((i) => Math.min(i + 1, activeSteps.length - 1));
  }, [activeSteps.length]);

  const goPrev = useCallback(() => {
    setStepIdx((i) => Math.max(i - 1, 0));
  }, []);

  return {
    isOpen,
    stepIdx,
    steps: activeSteps,
    showWelcome,
    startTour,
    finishTour,
    dismissWelcome,
    goNext,
    goPrev
  };
}

export type ScreenTourState = ReturnType<typeof useScreenTour>;
